import * as React from 'react';
import { JRCButton, JRCButtonProps } from './JRCButton';
import { useIntl } from 'react-intl';
import { DB_CLICK_DELAY, RESET_CLICK_DELAY } from '../../utils/const';

/**
 * @see JRCButtonProps
 * @member onValidationClick, optional callback called when the first click is made
 * @member validationMessage, optional validation message, default to "Confirm / Valider"
 */
export type JRCValidationButtonProps = JRCButtonProps & {
    onValidationClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    validationMessage?: string;
};

/** double click prevented by using a temporary state (PREVENT_DB_CLICK), changed after a timeout */
const state = {
    NOT_CLICKED: 'NOT_CLICKED',
    PREVENT_DB_CLICK: 'PREVENT_DB_CLICK',
    CLICKED: 'CLICKED',
};

export const JRCValidationButton = ({
    onClick,
    onValidationClick,
    children,
    color,
    variant,
    icon,
    ...props
}: JRCValidationButtonProps) => {
    const intl = useIntl();

    const buttonRef = React.useRef<HTMLButtonElement>(null);

    // use ref hack to access current value in callback
    const [validation, setValidation] = React.useState(state.NOT_CLICKED);
    const validationRef = React.useRef<(typeof state)[keyof typeof state]>();
    validationRef.current = validation;

    const [timeoutId, setTimeoutId] = React.useState<number>();
    const timeoutIdRef = React.useRef<number>();
    timeoutIdRef.current = timeoutId;

    React.useEffect(() => {
        if (validation === state.PREVENT_DB_CLICK) {
            setTimeoutId(
                setTimeout(() => {
                    setValidation(state.NOT_CLICKED);
                    buttonRef?.current?.removeAttribute('style');
                }, RESET_CLICK_DELAY) as unknown as number,
            );
        }
    }, [validation]);

    const validationOnClick = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            if (validationRef.current === state.CLICKED) {
                // Timeout of 0 second to delay the change at the end of the execution pile
                setTimeout(() => setValidation(state.NOT_CLICKED), 0);
                buttonRef?.current?.removeAttribute('style');
                onClick && onClick(e);
                if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
            } else {
                setValidation(state.PREVENT_DB_CLICK);
                setTimeout(() => setValidation(state.CLICKED), DB_CLICK_DELAY);
                const width = buttonRef?.current?.getBoundingClientRect()?.width;
                buttonRef?.current?.setAttribute('style', `min-width: ${width}px;`);
                onValidationClick && onValidationClick(e);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onClick],
    );

    return (
        <JRCButton
            {...props}
            type={validation === state.CLICKED ? props.type : 'button'}
            ref={buttonRef}
            onClick={validationOnClick}
            icon={validation === state.NOT_CLICKED ? icon : 'icon-fs-question'}
            color={color}
            variant={validation === state.NOT_CLICKED ? variant : 'outlined'}>
            {validation === state.NOT_CLICKED
                ? children
                : props.validationMessage || intl.formatMessage({ id: 'GLOBAL_Validation' })}
        </JRCButton>
    );
};
